import type { Coordinates, FileWithInfoLocal } from "features/dt-basic-setup/single-building/model/types";

import { extractEpcDocument } from "features/dt-basic-setup/single-building/api/epc";
import { SingleBuildingContext } from "features/dt-basic-setup/single-building/context/AddSingleBuildingContext";
import {
  enrichEpcExtractionWithGeocodedCountry,
  geocodeEpcExtractionAddress,
} from "features/dt-basic-setup/single-building/lib/epcGeocoding";
import { applyEpcExtractionToFormData } from "features/dt-basic-setup/single-building/lib/epcToFormData";
import { getErrorMessage, isAbortError } from "features/shared/lib/axios";
import { useCallback, useContext, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { createId } from "utils/general";

import { ENERGY_CERTIFICATE_SECTION, EPC_ACCEPTED_EXTENSIONS, UPLOAD_COMPLETE_PERCENT } from "../epc-modal/constants";
import { EpcExtractionData, EpcModalPhase } from "../epc-modal/types";

export const useEpcUpload = ({ isEnabled, preserveAddress }: { isEnabled: boolean; preserveAddress: boolean }) => {
  const { t } = useTranslation();
  const { callForRecenter, dismissEpcPrompt, formData, hasDismissedEpcPrompt, setFormData } =
    useContext(SingleBuildingContext);
  const abortRef = useRef<AbortController | null>(null);

  const hasStoredExtraction = !!formData.epcExtractionData;
  const shouldAutoOpenModal = isEnabled && !hasStoredExtraction && !hasDismissedEpcPrompt;

  const [modalOpen, setModalOpen] = useState(shouldAutoOpenModal);
  const [phase, setPhase] = useState<EpcModalPhase>(hasStoredExtraction ? "preview" : "intro");
  const [file, setFile] = useState<File | null>(null);
  const [uploadProgress, setUploadProgress] = useState<null | number>(null);
  const [extractionError, setExtractionError] = useState<null | string>(null);
  const [pendingExtraction, setPendingExtraction] = useState<EpcExtractionData | null>(
    formData.epcExtractionData ?? null
  );
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [pendingGeocoded, setPendingGeocoded] = useState<Coordinates | null>(null);

  const isBusy = phase === "uploading" || phase === "analyzing";
  const fallbackErrorMessage = t("AddBuilding.epcModal.error.extractionFailed");

  const abortActiveUpload = useCallback(() => {
    abortRef.current?.abort();
    abortRef.current = null;
    setUploadProgress(null);
  }, []);

  const handleCloseModal = useCallback(() => {
    if (phase === "uploading" || phase === "analyzing") {
      abortActiveUpload();
    }
    dismissEpcPrompt();
    setModalOpen(false);
  }, [abortActiveUpload, dismissEpcPrompt, phase]);

  const handleSkip = useCallback(() => {
    handleCloseModal();
  }, [handleCloseModal]);

  const handleStartUpload = useCallback(() => {
    setExtractionError(null);
    setPhase("upload");
  }, []);

  const handleBackToIntro = useCallback(() => {
    if (isBusy) return;
    setFile(null);
    setPendingGeocoded(null);
    setExtractionError(null);
    setPhase("intro");
  }, [isBusy]);

  const cancelUpload = useCallback(() => {
    abortActiveUpload();
    setPhase("upload");
  }, [abortActiveUpload]);

  const runUploadAndExtract = useCallback(
    async (selectedFile: File) => {
      abortRef.current?.abort();
      const abortController = new AbortController();
      abortRef.current = abortController;
      setPhase("uploading");
      setUploadProgress(0);
      setExtractionError(null);

      try {
        const response = await extractEpcDocument(selectedFile, {
          onUploadProgress: ({ percentage }) => {
            setUploadProgress(percentage);
            setPhase(percentage >= UPLOAD_COMPLETE_PERCENT ? "analyzing" : "uploading");
          },
          signal: abortController.signal,
        });

        if (!response.success || !response.data) {
          throw new Error(response.message || fallbackErrorMessage);
        }

        if (abortController.signal.aborted) return;

        setPhase("analyzing");

        const geocoded = await geocodeEpcExtractionAddress(response.data, abortController.signal);
        if (abortController.signal.aborted) return;

        const epcAddress =
          response.data.suggestedFields?.address ?? response.data.page1?.building?.address ?? undefined;
        if (epcAddress && !geocoded) {
          toast.error(t("AddBuilding.addressStep.geocode.locationError"));
        }

        const enrichedExtraction = enrichEpcExtractionWithGeocodedCountry(response.data, geocoded?.country);
        setFormData((prev) => ({ ...prev, epcRawExtractionData: response.data }));
        setPendingExtraction(enrichedExtraction);
        setPendingGeocoded(geocoded ? { lat: geocoded.lat, lng: geocoded.lng } : null);
        setPendingFile(selectedFile);
        setPhase("preview");
        setUploadProgress(null);
        setExtractionError(null);
      } catch (error) {
        if (isAbortError(error)) return;
        const message = getErrorMessage(error, fallbackErrorMessage);
        setExtractionError(message);
        toast.error(message);
        setPhase("upload");
        setUploadProgress(null);
      } finally {
        if (abortRef.current === abortController) {
          abortRef.current = null;
        }
      }
    },
    [fallbackErrorMessage, setFormData, t]
  );
  const handleFileChange = useCallback((next: File | null) => {
    setFile(next);
    setExtractionError(null);
  }, []);

  const handleSubmitUpload = useCallback(() => {
    if (!file) {
      const message = t("AddBuilding.epcModal.selectFileFirst");
      setExtractionError(message);
      return;
    }
    runUploadAndExtract(file);
  }, [file, runUploadAndExtract, t]);

  const handleApply = useCallback(() => {
    if (!pendingExtraction || !pendingFile) return;

    setFormData((prev) => {
      const prevFiles = { ...(prev.files ?? {}) };
      const fileEntry: FileWithInfoLocal = { file: pendingFile, id: createId(), source: "local" };
      const existingEpcFiles = prevFiles[ENERGY_CERTIFICATE_SECTION] ?? [];
      const pendingFileName = pendingFile.name;
      const dedupedEpcFiles = existingEpcFiles.filter((entry) => entry.file.name !== pendingFileName);
      const withFile = {
        ...prev,
        files: {
          ...prevFiles,
          [ENERGY_CERTIFICATE_SECTION]: [...dedupedEpcFiles, fileEntry],
        },
      };
      const applied = applyEpcExtractionToFormData(withFile, pendingExtraction, {
        preserveAddress,
      });
      return pendingGeocoded && !preserveAddress ? { ...applied, buildingCenter: pendingGeocoded } : applied;
    });

    if (pendingGeocoded) {
      callForRecenter();
    }

    setModalOpen(false);
  }, [callForRecenter, pendingExtraction, pendingFile, pendingGeocoded, preserveAddress, setFormData]);

  const handleBackToUpload = useCallback(() => {
    if (isBusy) return;
    setPendingGeocoded(null);
    setPhase("upload");
  }, [isBusy]);

  const openModal = useCallback(() => {
    setModalOpen(true);
    if (pendingExtraction || formData.epcExtractionData) {
      setPendingExtraction((current) => current ?? formData.epcExtractionData ?? null);
      setPhase("preview");
      return;
    }
    setPhase("intro");
  }, [formData.epcExtractionData, pendingExtraction]);

  return {
    handlers: {
      cancelUpload,
      handleApply,
      handleBackToIntro,
      handleBackToUpload,
      handleCloseModal,
      handleFileChange,
      handleSkip,
      handleStartUpload,
      handleSubmitUpload,
      openModal,
    },
    states: {
      acceptedExtensions: EPC_ACCEPTED_EXTENSIONS,
      extractionError,
      file: file ?? pendingFile,
      hasAppliedEpc: hasStoredExtraction,
      modalOpen,
      pendingExtraction,
      phase,
      selectedFile: file,
      uploadProgress,
    },
  };
};
